import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, absoluteUrl } from "@/lib/api";
import { getByPath as getByPathUtil, setByPath as setByPathUtil } from "@/lib/pathUtils";
import { useAuth } from "@/contexts/AuthContext";

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const { admin } = useAuth();
  const [content, setContent] = useState(null);
  const [editMode, setEditModeState] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchContent = useCallback(async () => {
    const { data } = await api.get("/content");
    setContent(data);
    setDirty(false);
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  useEffect(() => {
    if (!admin) setEditModeState(false);
  }, [admin]);

  useEffect(() => {
    if (!content) return;
    document.documentElement.style.setProperty("--crimson", content.colors.crimson);
    document.documentElement.style.setProperty("--vermilion", content.colors.crimson);
    document.documentElement.style.setProperty("--ink", content.colors.ink);
    document.documentElement.style.setProperty("--parchment", content.colors.parchment);
  }, [content?.colors]);

  function setEditMode(on) {
    setEditModeState(on);
  }

  function setPath(path, value) {
    setContent((prev) => setByPathUtil(prev, path, value));
    setDirty(true);
  }

  async function save() {
    if (!content) return;
    setSaving(true);
    try {
      const { id, _id, ...body } = content;
      const { data } = await api.put("/content", body);
      setContent(data);
      setDirty(false);
      toast.success("Saved — visible to all visitors");
    } catch (e) {
      toast.error("Save failed, try again");
    } finally {
      setSaving(false);
    }
  }

  async function uploadImage(file) {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/upload", form);
    return data.url;
  }

  return (
    <ContentContext.Provider
      value={{
        content,
        editMode,
        setEditMode,
        setPath,
        save,
        dirty,
        saving,
        uploadImage,
        getByPath: (p) => getByPathUtil(content, p),
        refetch: fetchContent,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  return useContext(ContentContext);
}

export { absoluteUrl };
